import { Stack } from 'components/Stack'
import { useCallback, memo, useRef, useEffect, useState, useMemo } from 'react'
import { BubblehouseContainer } from './Bubblehouse.styles'

interface BubblehouseProps {
  bubblehouseShopSlug: string
  token?: string
  setIsIframeLoaded?: (isIframeLoaded: boolean) => void
  isIframeLoaded?: boolean
}

const BubblehouseComponent = ({
  bubblehouseShopSlug,
  token,
  setIsIframeLoaded,
  isIframeLoaded,
}: BubblehouseProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const hasLoadedRef = useRef(false)
  const [bubblehouseLoaded, setBubblehouseLoaded] = useState(false)
  const [dynamicHeight, setDynamicHeight] = useState<string>('1500px')

  // Function to adjust iframe height based on content
  const adjustIframeHeight = useCallback(() => {
    if (iframeRef.current && bubblehouseLoaded) {
      try {
        const iframe = iframeRef.current
        // Try to get the content height from the iframe
        const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document
        if (iframeDocument) {
          const contentHeight = Math.max(
            iframeDocument.documentElement.scrollHeight,
            iframeDocument.body.scrollHeight,
            iframeDocument.documentElement.offsetHeight,
            iframeDocument.body.offsetHeight
          )
          if (contentHeight > 0) {
            setDynamicHeight(`${contentHeight}px`)
          }
        }
      } catch (error) {
        // Cross-origin restrictions might prevent access to iframe content
        // Fall back to using the predefined height
        console.log('Cannot access iframe content, using fallback height')
        setDynamicHeight('1500px')
      }
    }
  }, [bubblehouseLoaded])

  const handleIframeLoad = useCallback(() => {
    console.log('handleIframeLoad')
    if (!hasLoadedRef.current) {
      console.log('set to true')
      hasLoadedRef.current = true
      setIsIframeLoaded?.(true)
      setBubblehouseLoaded(true)

      // Set initial height and try to adjust
      setDynamicHeight('1500px')
      setTimeout(adjustIframeHeight, 1000) // Give some time for content to render
    }
  }, [setIsIframeLoaded, setBubblehouseLoaded, adjustIframeHeight])

  // Listen for resize events from the iframe content
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Listen for height messages from the iframe content
      if (event.data && typeof event.data === 'object' && event.data.height) {
        setDynamicHeight(`${event.data.height}px`)
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  // Prevent iframe from reloading by maintaining its src
  useEffect(() => {
    if (iframeRef.current && !hasLoadedRef.current) {
      const iframe = iframeRef.current
      const src = token
        ? `https://app.bubblehouse.com/s/${bubblehouseShopSlug}/blocks/referral?auth=${token}&instance=embed&preview=1`
        : `https://app.bubblehouse.com/s/${bubblehouseShopSlug}/blocks/referral`

      if (iframe.src !== src) {
        iframe.src = src
      }
    }
  }, [bubblehouseShopSlug, token])

  console.log('hasLoadedRef.current', hasLoadedRef)

  return (
    <BubblehouseContainer>
      <iframe
        ref={iframeRef}
        id="bubblehouse-referrals"
        data-testid="bubblehouse-referrals"
        title="Bubblehouse Referral Program"
        loading="lazy"
        onLoad={handleIframeLoad}
        sandbox="allow-top-navigation allow-scripts allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox allow-same-origin"
        allow="clipboard-write"
        style={{
          border: '0',
          width: '100%',
          height: bubblehouseLoaded ? dynamicHeight : '0px',
        }}
      />
    </BubblehouseContainer>
  )
}

const areEqual = (prevProps: BubblehouseProps, nextProps: BubblehouseProps) => {
  return (
    prevProps.bubblehouseShopSlug === nextProps.bubblehouseShopSlug &&
    prevProps.token === nextProps.token &&
    prevProps.isIframeLoaded === nextProps.isIframeLoaded
  )
}

export const Bubblehouse = memo(BubblehouseComponent, areEqual)
